import React, { useRef, useState, useEffect } from 'react';
import Overlay from 'react-overlays/Overlay';

import { HIDE_OVERLAY_DELAY, SHOW_OVERLAY_DELAY, HOVER_SPEED_SENSITIVITY, MOUSE_SPEED_MEASURE_INTERVAL } from '../../../lib/constants';
import { useIsScrolling } from '../useIsScrolling';
import { ActionType, Location, OverlayWrapperProps } from './types';

const OverlayWrapper: React.FC<OverlayWrapperProps> = (props) => {
  const {
    action,
    Content,
    contentProps,
    offset,
    onHide,
    placement,
    rootClose,
    show: defaultShow,
    Target,
    targetProps,
    className,
    showDelay,
    hideDelay,
    allowHoverDuringScroll,
    containerRef,
    scrollContainerRef,
  } = props;

  let hideTimeout: NodeJS.Timeout, showTimeout: NodeJS.Timeout;
  let checkSpeedInterval: NodeJS.Timeout;
  const targetRef = useRef(null);
  const isScrolling = useIsScrolling(scrollContainerRef);

  const [show, setShow] = useState<boolean>(false);

  let currTime: number, prevTime: number, currLoc: Location, prevLoc: Location;
  let isOverElement: boolean;

  useEffect(() => {
    if (defaultShow !== undefined && !action) {
      setShow(defaultShow);
    }
  }, [defaultShow]);

  const handleInteraction = (actionType: ActionType, value?: boolean) => {
    if (action !== actionType) {
      return;
    }

    if (action === 'click') {
      setShow(!show);
    } else if (action === 'hover' && value === false) {
      clearTimeout(showTimeout);
      hideTimeout = setTimeout(() => {
        setShow(false);
      }, hideDelay || HIDE_OVERLAY_DELAY);
    } else if (action === 'hover' && value) {
      clearTimeout(hideTimeout);
      showTimeout = setTimeout(() => {
        setShow(true);
      }, showDelay || SHOW_OVERLAY_DELAY);
    }
  };
  
  const trackSpeed = () => {
    let speed;

    if (!currTime || currTime === prevTime) {
      speed = 0;
    } else if (prevLoc && currLoc && prevTime && currTime) {
      speed = Math.sqrt(
        Math.pow(prevLoc.x - currLoc.x, 2) +
        Math.pow(prevLoc.y - currLoc.y, 2),
      ) / (currTime - prevTime);
    }

    if (speed < HOVER_SPEED_SENSITIVITY) {
      clearInterval(checkSpeedInterval);
      handleInteraction('hover', true);
    } else {
      prevLoc = currLoc;
      prevTime = currTime;
    }
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isOverElement === true) {
      return;
    }
    isOverElement = true;

    prevLoc = {
      x: event.pageX,
      y: event.pageY,
    };
    prevTime = Date.now();
    checkSpeedInterval = setInterval(trackSpeed, MOUSE_SPEED_MEASURE_INTERVAL);
  };

  const handleMouseLeave = () => {
    isOverElement = false;
    clearInterval(checkSpeedInterval);
    handleInteraction('hover', false);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!prevLoc) {
      return;
    }
    currLoc = {
      x: event.pageX,
      y: event.pageY,
    };
    currTime = Date.now();
  };

  if (allowHoverDuringScroll === false) {
    if (isScrolling) {
      handleInteraction('hover', false);
    }
  }

  return (
    <>
      <div 
        className={className}
        onClick={() => handleInteraction('click')}
        onMouseLeave={handleMouseLeave}
        onMouseOver={(event) => handleMouseOver(event)}
        onMouseMove={(event) => handleMouseMove(event)}
      >
        <Target
          {...targetProps}
          ref={targetRef}
        />
      </div>
      <Overlay
        offset={offset}
        onHide={onHide}
        placement={placement}
        rootClose={rootClose}
        show={show}
        target={targetRef}
        container={containerRef}
      >
        {({ props }) => (
          <Content
            {...props}
            {...contentProps}
            onMouseLeave={() => handleInteraction('hover', false)}
            onMouseEnter={() => handleInteraction('hover', true)}
          />
        )}
      </Overlay>
    </>
  );
};

export default OverlayWrapper;
